// THE LINE-ITEM BULK BAR, AS DATA (owner, 2026-09-29 — `docs/decisions.md`
// D-228 to D-232). The arithmetic and the plans behind `LineItemBulkBar.tsx`,
// kept out of the components so the tests can call them.
//
// *"In the invoice modal, users need a way to bulk-apply Account and
// Department values … An Account would be necessary to enable that button, but
// a Department would not be."* And: *"maybe add an option for the user to
// choose between applying those selections to existing selected rows and
// collapsing all selected rows into one new row with the selected Account and
// Department values … use averages or sums or whatever when collapsing Price
// and Quantity so the Total does not change by default."*
//
// Five modes. APPLY writes the chosen Account, and the Department if one was
// chosen, onto every selected line, through the write each line's own pickers
// use. GROUP MAKES A GROUP (owner, 2026-10-22 — D-396, D-399, G-20; the option
// read Combine until 2026-10-23, D-407, and its stored value stays `combine`): the
// selected lines stay, under a new group named by the Description; its Total is
// the sum of their totals, its Price that Total and its Quantity 1, all locked
// (D-242, G-6), and an Account, Department or Marketing Category chosen is
// written to every line in it (G-7). REMOVE FROM GROUP takes the selected lines
// out of their groups, and MOVE TO GROUP puts them in the group picked; a moved
// line keeps its own values (G-20). The three grouping modes are offered
// wherever grouping may change (`groupingOpen`, G-12). DELETE (owner,
// 2026-10-23 — D-407) removes every selected line as its own trash button
// would, offered only where that button is.
//
// DEPARTMENT-ONLY APPLY (owner, 2026-10-07 — D-305). Where the route lock
// leaves a line's Department open and its Account locked — the Manager route,
// from Manager Approval through Manual Entry — Apply writes the chosen
// Department alone and never the Account (`bulkApplyScope`). The bulk row
// exists because approval is refused until every line is answered (D-228);
// locking the Account must not send those steps back to one picker per line.
//
// THE BAR IS A ROW OF THE TABLE (owner, 2026-09-30 — D-242): *"I would like
// the Description, Account, Department, Price, Quantity, and Total values to
// sit above where they normally sit, as closely as possible."* Every field
// shows once a line is checked; the ones the mode does not use are disabled
// (`bulkFieldsEnabled`).

import { useEffect, useMemo, useState } from 'react'
import { groupTagKey } from '@ap/shared'
import { lineTotal } from './format'
import type { LineItemState } from './intakeSaveBody'
import type { AssociatedEntity } from './types'

export type BulkMode = 'apply' | 'combine' | 'remove' | 'move' | 'delete'

/** The mode picker's options (D-242: *"the renamed Apply/Combine picker"*;
    the two grouping modes since D-399). The first reads Update since
    2026-10-14 (owner), so that Apply can be the button; its stored value stays
    `apply`. The second reads Group since 2026-10-23 (owner, D-407: "The
    Combine action should now be called Group"); its value stays `combine`.
    Delete since the same day (D-407). */
export const BULK_MODE_OPTIONS: readonly { value: BulkMode; label: string }[] = [
  { value: 'apply', label: 'Update' },
  { value: 'combine', label: 'Move to New Group' },
  { value: 'remove', label: 'Ungroup' },
  { value: 'move', label: 'Move to Existing Group' },
  { value: 'delete', label: 'Delete' },
]

/**
 * WHICH MODES THE CHECKED LINES ALLOW (D-399, G-20). Update always; and where
 * grouping may change here: Group for one or more lines (D-423), Ungroup
 * when one of them sits in a group, Move to Existing Group when some group is not
 * already every checked line's. Delete wherever a line's own trash button is
 * offered (`deletable`, D-407).
 */
export function bulkModesOffered(input: {
  grouping: boolean
  /** A line's trash button is offered here: the editor, never a locked source (D-380). */
  deletable?: boolean
  /** Each checked line's group key, or null when it stands alone. */
  selectedGroups: readonly (string | null)[]
  /** Every group on the invoice, by key. */
  groupKeys: readonly string[]
}): BulkMode[] {
  const modes: BulkMode[] = ['apply']
  if (input.grouping) {
    // Move to New Group with a single line too (owner, 2026-10-28 — D-423).
    if (input.selectedGroups.length >= 1) modes.push('combine')
    if (input.selectedGroups.some((g) => g !== null)) modes.push('remove')
    if (input.groupKeys.some((key) => !input.selectedGroups.every((g) => g === key))) modes.push('move')
  }
  if (input.deletable && input.selectedGroups.length > 0) modes.push('delete')
  return modes
}

/**
 * WHAT APPLY MAY WRITE, from which halves of a line's GL code are open here
 * (`glCodeEditable`, `lib/required.ts`):
 *
 * - `'account'`: the Account is open (Import Control, Controller Approval).
 *   Apply writes the Account, and the Department if one was chosen.
 * - `'department-only'`: the Department is open and the Account is not (the
 *   Manager route past Import Control — D-305). Apply writes the Department
 *   alone.
 * - `null`: neither is open. No boxes and no bulk row.
 */
export type BulkApplyScope = 'account' | 'department-only'

export function bulkApplyScope(open: { account: boolean; department: boolean }): BulkApplyScope | null {
  if (open.account) return 'account'
  return open.department ? 'department-only' : null
}

/** Which of the bulk row's fields each mode uses. The rest show, disabled (D-242). */
interface BulkFieldsEnabled {
  description: boolean
  account: boolean
  department: boolean
  /** Price, Quantity and Total: locked values in Combine, disabled in Apply. */
  amounts: boolean
  /** The one icon, Additional Detail, which lists the lines' tags too (D-425). */
  additionalDetail: boolean
}

export function bulkFieldsEnabled(mode: BulkMode, scope: BulkApplyScope = 'account'): BulkFieldsEnabled {
  const combine = mode === 'combine'
  // Ungroup, Move to Existing Group and Delete use no Account or Department.
  const grouping = mode === 'remove' || mode === 'move' || mode === 'delete'
  return {
    // Combine's group name; Move to Existing Group's group picker sits in the same cell.
    description: combine || mode === 'move',
    // Department-only Apply shows the Account disabled: it is not a choice (D-305).
    account: scope === 'account' && !grouping,
    department: !grouping,
    amounts: combine,
    // UPDATE OWES IT TOO (owner, 2026-10-23 — D-415: "These should behave in
    // the same way"): an Update to a Marketing Account asks for the Marketing
    // Category, as Move to New Group does. Not on a Department-only Update.
    additionalDetail: combine || (mode === 'apply' && scope === 'account'),
  }
}

/** A money value in whole cents, or 0 for a blank or unreadable one. */
function cents(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0
  if (typeof value === 'string' && value.trim() === '') return 0
  const n = Number(value)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

/** The selected lines' totals added up in whole cents, as a two-place string. */
export function sumTotals(totals: readonly (number | string | null | undefined)[]): string {
  const sum = totals.reduce<number>((acc, t) => acc + cents(t), 0)
  return (sum / 100).toFixed(2)
}

/**
 * COMBINE'S VALUES: the Total is the sum of the selected totals, the Price is
 * that Total and the Quantity is 1 (owner, 2026-09-30 — D-242: *"keep the
 * Total equal to the sum of the selected rows' combined Total. Make Price
 * equal that new Total, and set Quantity to 1."*). All three are locked.
 * Summing in whole cents keeps it exact: $0.10 + $0.20 is $0.30 here, not
 * $0.30000000000000004.
 */
export function combineDefaults(totals: readonly (number | string | null | undefined)[]): {
  price: string
  quantity: string
  total: string
} {
  const price = sumTotals(totals)
  return { price, quantity: '1', total: lineTotal(price, '1') }
}

/**
 * WHAT THE BUTTON STILL NEEDS, as its tooltip says it — the same kind of
 * sentence Release's tooltip gives. `undefined` once nothing is missing.
 */
export function bulkBlockedReason(input: {
  mode: BulkMode
  accountId: string | undefined
  description: string
  /** A Marketing Category the Account owes is empty: Move to New Group (D-242), Update since D-415. */
  additionalDetailMissing?: boolean
  /** Department-only Apply (D-305): the Department is what is required, and the Account is never asked for. */
  scope?: BulkApplyScope
  departmentId?: string
  /** Move to Existing Group: the group picked. */
  groupKey?: string
}): string | undefined {
  const missing: string[] = []
  if (input.mode === 'remove' || input.mode === 'delete') return undefined
  if (input.mode === 'move') return input.groupKey ? undefined : 'Choose a group first.'
  if (input.mode === 'combine') {
    // A GROUP NEEDS A NAME, and nothing else (D-399): its lines keep their own
    // values, and an Account or Department chosen here is written to each of
    // them (G-7). A group whose lines disagree shows red until they agree (G-8).
    if (input.description.trim() === '') missing.push('a Description')
    // The Price and Quantity are locked (D-242), so they are never missing.
    if (input.additionalDetailMissing) missing.push('the Additional Detail')
  } else if (input.scope === 'department-only') {
    if (!input.departmentId) missing.push('a Department')
  } else if (!input.accountId) missing.push('an Account')
  // UPDATE OWES IT AS MOVE TO NEW GROUP DOES (D-415).
  else if (input.additionalDetailMissing) missing.push('the Additional Detail')
  if (missing.length === 0) return undefined
  const list =
    missing.length === 1
      ? missing[0]
      : `${missing.slice(0, -1).join(', ')}${missing.length > 2 ? ',' : ''} and ${missing[missing.length - 1]}`
  return `Choose ${list} first.`
}

/**
 * WHAT APPLY DOES TO ONE LINE'S DEPARTMENT — what a pick on the line's own
 * Account picker does (owner, 2026-09-30, D-242, replacing D-229's keep):
 *
 * - A Department was chosen: that one (`set`).
 * - None was chosen and the Account is not changing: nothing (`leave`).
 * - Otherwise: the Account write's own rule (`cascade`) — the Department is
 *   cleared, or filled where the new Account admits exactly one. A Department
 *   that would also fit the new Account is cleared too, as on a row.
 */
type ApplyDepartmentPlan =
  | { kind: 'set'; departmentId: string }
  | { kind: 'leave' }
  | { kind: 'cascade' }

export function departmentPlanForApply(input: {
  chosenDepartmentId: string | undefined
  currentAccountId: string | null | undefined
  currentDepartmentId: string | null | undefined
  newAccountId: string
}): ApplyDepartmentPlan {
  if (input.chosenDepartmentId) return { kind: 'set', departmentId: input.chosenDepartmentId }
  if ((input.currentAccountId ?? null) === input.newAccountId) return { kind: 'leave' }
  return { kind: 'cascade' }
}

/** The one Account every checked line holds, or `null` when they differ or any has none (D-242). */
export function sharedAccountId(accountIds: readonly (string | null | undefined)[]): string | null {
  const first = accountIds[0]
  if (!first) return null
  return accountIds.every((id) => id === first) ? first : null
}

/** An Apply or Combine with an Account: the chosen Account, and the Department if one was chosen. */
export interface AccountBulkChoice {
  accountId: string
  accountLabel: string
  departmentId?: string
  departmentLabel?: string
  /** A Marketing Category written to every line, when the Account owes one (Update since D-415). */
  marketing?: { id: string; label: string; context: string | null }
}

/** A Department-only Apply (D-305): the Department alone. No Account is written. */
interface DepartmentOnlyBulkChoice {
  accountId?: undefined
  departmentId: string
  departmentLabel: string
}

/** What the bar's Apply hands its caller. */
export type BulkChoice = AccountBulkChoice | DepartmentOnlyBulkChoice

/**
 * APPLY, AT IMPORT CONTROL: the editor's rows with the choice written onto the
 * selected ones. The editor's next save sends them, as it would a pick on each
 * row: a cascaded line's Department is cleared on screen, and the server
 * derives the sole one for the new Account where there is one. A
 * Department-only choice sets the Department and leaves the Account.
 */
export function applyToRows(
  rows: readonly LineItemState[],
  selectedIds: ReadonlySet<string>,
  choice: BulkChoice,
): LineItemState[] {
  return rows.map((r) => {
    if (!selectedIds.has(r.lineItemId)) return r
    if (choice.accountId === undefined) {
      return { ...r, departmentId: choice.departmentId, departmentLabel: choice.departmentLabel, departmentPicked: true }
    }
    const plan = departmentPlanForApply({
      chosenDepartmentId: choice.departmentId,
      currentAccountId: r.accountId,
      currentDepartmentId: r.departmentId,
      newAccountId: choice.accountId,
    })
    const account = { accountId: choice.accountId, accountLabel: choice.accountLabel }
    switch (plan.kind) {
      case 'set':
        return { ...r, ...account, departmentId: plan.departmentId, departmentLabel: choice.departmentLabel, departmentPicked: true }
      case 'leave':
        return { ...r, ...account }
      case 'cascade':
        return { ...r, ...account, departmentId: undefined, departmentLabel: undefined, departmentPicked: false }
    }
  })
}

/**
 * WHAT COMBINE MAKES (D-399): a group named `description` over the checked
 * lines. An Account (with a Department) or a Marketing Category chosen in the
 * bulk row is written to every line in it (G-7); left out, each line keeps its
 * own.
 */
export interface CombineInput {
  description: string
  choice?: AccountBulkChoice
  marketing?: { id: string; label: string; context: string | null }
}

/** One selected line's Additional Detail, as the bulk row reads them. */
export interface SelectedDetail {
  marketingCategoryId?: string | null
  marketingLabel?: string
}

/** The one value every selected line holds, or `null` — the server's `agreedValue`. */
function agreed<T>(values: readonly (T | null | undefined)[]): T | null {
  if (values.length === 0) return null
  const first = values[0]
  if (first === null || first === undefined) return null
  return values.every((v) => v === first) ? first : null
}

/**
 * THE PROPOSED GROUP'S STARTING ADDITIONAL DETAIL: the value every selected
 * line agrees on (G-8's "agree"), so the row starts from what the group would
 * show. A category chosen here is written to every line in the group.
 */
export function agreedDetails(lines: readonly SelectedDetail[]): {
  marketing: { id: string | null; label: string }
} {
  const marketingId = agreed(lines.map((l) => l.marketingCategoryId))
  return {
    marketing: { id: marketingId, label: marketingId ? (lines[0]?.marketingLabel ?? marketingId) : '—' },
  }
}

/**
 * EVERY TAG ON A SET OF LINES, one per entity, in the order given — what a
 * group's Associated Entities icon lists (its lines keep their own tags, G-11;
 * nothing is copied, review finding 7).
 */
export function combinedEntities<T extends AssociatedEntity>(tags: readonly T[], lineIds: ReadonlySet<string>): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const t of tags) {
    if (t.is_active === false || !t.child_id || !lineIds.has(t.child_id)) continue
    // Which entity a tag names, as the group rule compares it (D-425).
    const key = groupTagKey(t)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(t)
  }
  return out
}

/** Joins line numbers as the bar's messages say them: "1", "1 and 2", "1, 2 and 3". */
function lineList(numbers: readonly (number | null | undefined)[]): string {
  const shown = numbers.map((n) => String(n ?? '—'))
  if (shown.length <= 1) return shown.join('')
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`
}

/** One line APPLY writes past Import Control, and what it was when chosen. */
export interface ApplyLine {
  id: string
  line_number?: number | null
  row_version: number
  account_id?: string | null
  department_id?: string | null
  total?: number | string | null
}

/** The two per-line writes APPLY uses past Import Control — the ones the row pickers use. */
interface ApplyWrites {
  account: (line: ApplyLine, accountId: string) => Promise<{ applied: boolean; reason?: string; line?: ApplyLine }>
  department: (line: ApplyLine, departmentId: string) => Promise<{ applied: boolean; reason?: string; line?: ApplyLine }>
}

/**
 * APPLY, PAST IMPORT CONTROL. There is no bulk route for the Account, so each
 * line gets the same writes its own pickers make, one line at a time: the
 * Account when it changes, then the Department when the plan names one the
 * line does not already hold. A Department-only choice (D-305) makes the
 * Department write alone and never calls the Account route. Each write records
 * its own correction, exactly as a one-by-one edit does. It stops at the first
 * refusal and says which lines landed, which one was refused and why, and
 * which were not reached.
 */
export async function applyLinesInTurn(
  lines: readonly ApplyLine[],
  choice: BulkChoice,
  writes: ApplyWrites,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const done: ApplyLine[] = []
  for (const [i, original] of lines.entries()) {
    let line = original
    const plan: ApplyDepartmentPlan =
      choice.accountId === undefined
        ? { kind: 'set', departmentId: choice.departmentId }
        : departmentPlanForApply({
            chosenDepartmentId: choice.departmentId,
            currentAccountId: line.account_id,
            currentDepartmentId: line.department_id,
            newAccountId: choice.accountId,
          })
    let refusal: string | undefined
    if (choice.accountId !== undefined && (line.account_id ?? null) !== choice.accountId) {
      const res = await writes.account(line, choice.accountId)
      if (!res.applied) refusal = res.reason ?? 'The change was rejected.'
      else if (res.line) line = res.line
    }
    if (refusal === undefined && plan.kind === 'set' && line.department_id !== plan.departmentId) {
      const res = await writes.department(line, plan.departmentId)
      if (!res.applied) refusal = res.reason ?? 'The change was rejected.'
    }
    if (refusal !== undefined) {
      const rest = lines.slice(i + 1)
      const parts = [
        done.length > 0 ? `Applied to line${done.length === 1 ? '' : 's'} ${lineList(done.map((l) => l.line_number))}.` : '',
        `Line ${original.line_number ?? '—'} was refused: ${refusal.replace(/\.$/, '')}.`,
        rest.length > 0
          ? `Line${rest.length === 1 ? '' : 's'} ${lineList(rest.map((l) => l.line_number))} ${rest.length === 1 ? 'was' : 'were'} not changed.`
          : '',
      ]
      return { ok: false, message: parts.filter(Boolean).join(' ') }
    }
    done.push(original)
  }
  return { ok: true }
}

/**
 * THE SELECTED LINES, by id. Resets when `resetKey` changes (the invoice), and
 * prunes itself to `liveIds` whenever the lines reload, so a removed line is
 * never sent.
 */
export function useLineSelection(liveIds: readonly string[], resetKey: string | undefined) {
  const [selected, setSelected] = useState<readonly string[]>([])
  const liveKey = liveIds.join('|')
  useEffect(() => {
    setSelected([])
  }, [resetKey])
  useEffect(() => {
    const live = new Set(liveKey.split('|'))
    setSelected((prev) => (prev.every((id) => live.has(id)) ? prev : prev.filter((id) => live.has(id))))
  }, [liveKey])
  const selectedSet = useMemo(() => new Set(selected), [selected])
  return {
    selectedIds: selectedSet,
    count: selected.length,
    /** The header box: checked when every line is, mixed when some are. */
    headerState: headerCheckState(selected.length, liveIds.length),
    toggle: (id: string, on: boolean) =>
      setSelected((prev) => (on ? [...prev.filter((x) => x !== id), id] : prev.filter((x) => x !== id))),
    /** A group row's box (D-406): check or uncheck every one of its lines. */
    toggleMany: (ids: readonly string[], on: boolean) =>
      setSelected((prev) => (on ? [...prev.filter((x) => !ids.includes(x)), ...ids] : prev.filter((x) => !ids.includes(x)))),
    setAll: (on: boolean) => setSelected(on ? [...liveIds] : []),
    clear: () => setSelected([]),
  }
}

/** The header box's state for `count` of `total` selected. */
export function headerCheckState(count: number, total: number): boolean | 'mixed' {
  if (count > 0 && count === total) return true
  return count > 0 ? 'mixed' : false
}
